import type { MantineTheme } from '@mantine/core';
import get from 'lodash.get';

import {
  DepositOption,
  NEW_CARD_OPTION,
  depositOptionToValue,
} from '../deposits/components/consts';

function getAmount(depositOption: DepositOption, customAmount: number | undefined) {
  switch (depositOption) {
    case DepositOption.USD_25:
    case DepositOption.USD_50:
    case DepositOption.USD_100:
    case DepositOption.USD_200:
      return depositOptionToValue[depositOption];
    case DepositOption.CUSTOM:
    default:
      return customAmount;
  }
}

const getIconColor = (theme: MantineTheme) =>
  theme.colorScheme === 'dark' ? theme.colors.gray[4] : theme.colors.dark[6];

/** @deprecated */
const getTextColor = (theme: MantineTheme) =>
  theme.colorScheme === 'dark' ? theme.colors.gray[4] : theme.black;

const isNewCardOption = (cardOption: string) => cardOption === NEW_CARD_OPTION;

/**
 * Extracts a stable error code from an axios rejection. The CAG exception filter body is
 * `{ id, statusCode, message, error }` where `error` is the code; `message` is never shown.
 */
function getApiErrorCode(error: unknown): string {
  const filterCode: unknown = get(error, 'response.data.error');
  if (typeof filterCode === 'string' && filterCode.length > 0) return filterCode;
  const axiosCode: unknown = get(error, 'code');
  return typeof axiosCode === 'string' && axiosCode.length > 0 ? axiosCode : 'UNKNOWN';
}

/**
 * Gets error message translations with fallback support for dynamic error codes.
 *
 * When next-translate can't find a translation key, it returns the key itself.
 * This function checks if the translation exists by comparing the result with the key,
 * and falls back to UNKNOWN error translations if the key doesn't exist.
 *
 * @param t - The translation function from useTranslation
 * @param errorCode - The error code to get translations for (e.g., "ERROR_BAD_RESPONSE")
 * @returns An object with title and message translations
 *
 * @example
 * ```typescript
 * const { t } = useTranslation('payments');
 * const errorMessage = getErrorTranslations(t, error.code);
 * // Returns: { title: "...", message: "..." }
 * ```
 */
function getErrorTranslations(
  t: (key: string) => string,
  errorCode: string
): { title: string; message: string } {
  // eslint-disable-next-line local-rules/no-translate-cast
  const tUnsafe = t as (key: string) => string;

  const titleKey = `errors.${errorCode}.title`;
  const bodyKey = `errors.${errorCode}.body`;
  const messageKey = `errors.${errorCode}.message`;

  const titleTranslation = tUnsafe(titleKey);
  const bodyTranslation = tUnsafe(bodyKey);
  const messageTranslation = tUnsafe(messageKey);

  const message =
    (bodyTranslation !== bodyKey && bodyTranslation) ||
    (messageTranslation !== messageKey && messageTranslation) ||
    tUnsafe('errors.UNKNOWN.body');

  return {
    title: titleTranslation !== titleKey ? titleTranslation : tUnsafe('errors.UNKNOWN.title'),
    message,
  };
}

/**
 * Calculates the cash balance from wallet data.
 * Cash balance = availableBalance - credits
 *
 * @param walletData - The wallet balance data containing availableBalance and credits
 * @returns The cash balance, or 0 if wallet data is undefined
 */
function getCashBalance(walletData: { availableBalance?: number; credits?: number } | undefined) {
  if (!walletData) return 0;
  const { availableBalance = 0, credits = 0 } = walletData;
  return availableBalance - credits;
}

export {
  getAmount,
  getIconColor,
  getTextColor,
  isNewCardOption,
  getErrorTranslations,
  getApiErrorCode,
  getCashBalance,
};
